<template>
  <div class="c-integration-form container !pt-0">
    <el-form
        ref="integrationForm"
        :model="integrationForm"
        :rules="formRules"
        label-position="top"
        label-width="120px"
        @submit.native.prevent>

      <!-- Integration Name -->
      <el-row :class="integrationForm.target === 'ZAPIER' ? 'mb-6' : 'mb-4'" :gutter="20">
        <el-col :span="24">
          <el-form-item label="Integration Name" prop="name">
            <el-input v-model="integrationForm.name" class="gray" maxlength="255"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Zapier Button (Only for Zapier) -->
      <div v-if="integrationForm.target === 'ZAPIER'" class="mb-4">
        <button
            class="btn secondary outline medium m-auto mb-2"
            type="button"
            @click="openZapier">
          Open Zapier
        </button>
      </div>

      <!-- Webhook URL -->
      <el-row class="mb-2" :gutter="20">
        <el-col :span="24">
          <el-form-item :label="`${integrationForm.target === 'ZAPIER' ? 'Webhook' : 'Integration'} URL`" prop="url">
            <el-input v-model="integrationForm.url" class="gray"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Field Mapping -->
      <h3 class="mt-4 mb-2">Field Mapping</h3>
      <el-row class="mb-1" :gutter="20">
        <el-col :span="9">
          <el-form-item>
            <div slot="label" class="text-body-black">
              Internal Field
              <el-popover
                  placement="bottom"
                  trigger="hover"
                  content="This refers to the label names of the form elements used in your widget">
                <strong slot="reference">
                  <i class="el-icon-info"></i>
                </strong>
              </el-popover>
            </div>
          </el-form-item>
        </el-col>
        <el-col :span="2">
          <i class="el-icon-right text-xl opacity-0"></i>
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="allFieldsActive" @change="setAllFieldsActive(allFieldsActive)"></el-checkbox>
        </el-col>
        <el-col :span="11" :offset="1">
          <el-form-item>
            <div slot="label" class="text-body-black">
              Internal Field
              <el-popover
                  placement="bottom"
                  trigger="hover"
                  content="These are the field names that will be sent to Zapier">
                <strong slot="reference">
                  <i class="el-icon-info"></i>
                </strong>
              </el-popover>
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <hr class="my-2">

      <el-row
          v-for="(mapping, index) in integrationForm.mapping"
          class="mb-2"
          :key="index"
          :gutter="20"
          type="flex"
          align="middle">
        <el-col :span="9">
          <span class="text-base">{{ mapping.internal.name }}</span>
        </el-col>
        <el-col :span="2">
          <i class="el-icon-right text-xl"></i>
        </el-col>
        <el-col :span="1">
          <el-checkbox v-model="mapping.active" @change="checkAllFieldsActive"></el-checkbox>
        </el-col>
        <el-col :span="11" :offset="1">
          <el-form-item>
            <el-input v-model="mapping.external.name" class="gray" :disabled="!mapping.active"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <div class="mt-8 flex justify-end">
        <button class="btn secondary outline big mr-4" @click="cancel">Cancel</button>
        <button class="btn primary big" @click="saveChanges">Done</button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { errorMessages } from '@meta/errorMessages'
import validationMixin from '@components/mixins/validation'

export default {
  name: 'IntegrationForm',
  mixins: [validationMixin],
  props: {
    targetType: {
      type: String,
      required: true,
    },
  },
  data() {
    const checkValidURL = (rule, value, callback) => {
      this.integrationForm.url = this.setHttp(value)
      if (!value) {
        callback(new Error('Webhook URL is required'))
      } else {
        callback(this.validateURL(rule, this.integrationForm.url, callback))
      }
    }

    return {
      enableUpdate: false,
      errorMessages,
      integrationForm: {
        name: '',
        url: '',
        mapping: [],
        target: '', // ZAPIER or WEBHOOK (passed as a prop)
      },
      formRules: {
        name: [
          { required: true, message: 'Integration name is required', trigger: 'blur' },
          { max: 255, message: 'Integration name must be less than 255 characters', trigger: 'blur' },
        ],
        url: [
          { required: true, message: 'Webhook URL is required', trigger: 'blur' },
          { validator: checkValidURL, trigger: 'blur' },
        ],
      },
      allFieldsActive: false,
      formModified: false,
    }
  },
  computed: {
    ...mapState('integration', ['targetIntegrationID', 'integrations', 'integrationActionLoading']),
    ...mapGetters('campaign/widget', ['getWidgetFields']),
    integration() {
      return this.$store.getters['integration/targetIntegration']
    },
    targetWidgetID() {
      return this.$store.state.campaign.widget.targetWidgetID
    },
  },
  watch: {
    integrationForm: {
      handler() {
        if (this.enableUpdate) {
          this.formModified = true
          this.$emit('update', this.integrationForm)
        }
      },
      deep: true,
      immediate: false,
    },
    formModified(newValue) {
      this.$emit('form-modified', newValue)
    },
  },
  created() {
    this.integrationForm.target = this.targetType
    this.setFormValues(this.integration)
    this.$nextTick(() => {
      this.enableUpdate = true
    })
  },
  methods: {
    setAllFieldsActive(active) {
      this.integrationForm.mapping.forEach(item => {
        item.active = active
      })
    },
    saveChanges() {
      const saveAction = this.targetIntegrationID
          ? 'integration/updateIntegration'
          : 'integration/createIntegration'

      this.integrationForm.url = this.setHttp(this.integrationForm.url)

      this.$refs.integrationForm.validate((valid) => {
        if (!valid) return false

        this.$store.dispatch(saveAction, this.integrationForm)
            .then(() => {
              this.$message({ type: 'success', message: 'Save successful!' })
              this.$emit('force-close')
            })
            .catch((error) => {
              this.$message({ type: 'error', message: this.errorMessages.default })
              console.log(error)
            })
      })
    },
    setFormValues(integration) {
      if (integration) {
        this.integrationForm.name = integration.name || ''
        this.integrationForm.url = integration.url || ''
        this.integrationForm.mapping = JSON.parse(JSON.stringify(integration.mapping)) || []
      }

      this.integrationForm.widget = this.targetWidgetID
      this.syncWidgetFieldsWithMapping()
    },
    syncWidgetFieldsWithMapping() {
      const widgetFields = this.getWidgetFields
      this.integrationForm.mapping = this.integrationForm.mapping.map(mappingItem => {
        const matchingField = widgetFields.find(field => field.id === mappingItem.internal.fieldID)
        if (matchingField) {
          mappingItem.internal.fieldID = matchingField.id
        }
        return mappingItem
      }).filter(mappingItem => widgetFields.some(field => field.id === mappingItem.internal.fieldID))

      widgetFields.forEach(field => {
        if (!this.integrationForm.mapping.some(mappingItem => mappingItem.internal.fieldID === field.id)) {
          this.integrationForm.mapping.push({
            internal: { name: field.settings.label, fieldID: field.id },
            external: { name: field.settings.label },
            active: false,
          })
        }
      })
      this.checkAllFieldsActive()
    },
    checkAllFieldsActive() {
      this.allFieldsActive = this.integrationForm.mapping.every(item => item.active === true)
    },
    openZapier() {
      window.open('https://zapier.com/app/dashboard', '_blank')
    },
    cancel() {
      if (this.formModified) {
        this.$emit('close-drawer')
      } else {
        this.$emit('force-close')
      }
    },
  },
}
</script>

<style lang="scss">
.c-integration-form {
  .el-form .el-form-item {
    label {
      @apply font-medium;
    }
  }
}
</style>
